import React, { useState, useEffect, useContext, useCallback } from 'react';
import { FaPlus, FaEdit, FaTrash, FaFileImport, FaDownload, FaLock, FaPause, FaPlay, FaSpinner, FaClipboardList, FaUpload, FaFileExcel } from 'react-icons/fa';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { useSubscriptionCheck } from '../../components/SubscriptionGate';
import {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link as LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat, ChevronDown, ChevronRight
} from 'lucide-react';

// Section icons mapping
const SECTION_ICONS = {
  Ruler, Cog, Gauge, Wrench, Shield, Zap, Wifi, Thermometer, Award,
  Link: LinkIcon, Layers, Truck, Scale, FileCheck, MapPin, Star,
  GraduationCap, Activity, Box, Anchor, HardHat
};

// Dynamic Field Component
const DynamicField = ({ field, value, onChange, language }) => {
  const label = language === 'fr' ? field.label_fr : (field.label_en || field.label_fr);
  const helpText = language === 'fr' ? field.help_text_fr : (field.help_text_en || field.help_text_fr);
  
  const renderInput = () => {
    switch (field.field_type) {
      case 'text':
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        );
        
      case 'number':
        return (
          <div className="flex items-center gap-2">
            <Input
              type="number"
              value={value || ''}
              onChange={(e) => onChange(e.target.value)}
              placeholder={field.placeholder || ''}
              className="flex-1"
            />
            {field.unit && (
              <span className="text-sm text-gray-500 bg-gray-100 px-2 py-2 rounded">{field.unit}</span>
            )}
          </div>
        );
        
      case 'textarea':
        return (
          <textarea
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
            className="w-full min-h-[80px] p-3 border rounded-md resize-y text-sm"
          />
        );
        
      case 'select':
        return (
          <Select value={value || ''} onValueChange={onChange}>
            <SelectTrigger>
              <SelectValue placeholder={language === 'fr' ? 'Sélectionner...' : 'Select...'} />
            </SelectTrigger>
            <SelectContent>
              {(field.options || []).map((opt, idx) => (
                <SelectItem key={idx} value={opt}>{opt}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
        
      case 'multiselect':
        const selectedValues = Array.isArray(value) ? value : (value ? [value] : []);
        return (
          <div className="space-y-2">
            <div className="flex flex-wrap gap-2 p-2 border rounded-md min-h-[40px]">
              {selectedValues.map((v, idx) => (
                <Badge key={idx} variant="secondary" className="bg-[#635BFF]/10 text-[#635BFF]">
                  {v}
                  <button
                    type="button"
                    onClick={() => onChange(selectedValues.filter(sv => sv !== v))}
                    className="ml-1 hover:text-red-500"
                  >
                    ×
                  </button>
                </Badge>
              ))}
              {selectedValues.length === 0 && (
                <span className="text-gray-400 text-sm">{language === 'fr' ? 'Cliquez pour ajouter...' : 'Click to add...'}</span>
              )}
            </div>
            <div className="flex flex-wrap gap-1">
              {(field.options || []).filter(opt => !selectedValues.includes(opt)).map((opt, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onChange([...selectedValues, opt])}
                  className="text-xs px-2 py-1 bg-gray-100 hover:bg-[#635BFF]/10 hover:text-[#635BFF] rounded transition-colors"
                >
                  + {opt}
                </button>
              ))}
            </div>
          </div>
        );
        
      case 'boolean':
        return (
          <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
            <Switch
              checked={value === true || value === 'true'}
              onCheckedChange={(checked) => onChange(checked)}
            />
            <span className="text-sm">{value ? (language === 'fr' ? 'Oui' : 'Yes') : (language === 'fr' ? 'Non' : 'No')}</span>
          </div>
        );
        
      case 'date':
        return (
          <Input
            type="date"
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
          />
        );
        
      default:
        return (
          <Input
            value={value || ''}
            onChange={(e) => onChange(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        );
    }
  };
  
  return (
    <div className={field.field_type === 'multiselect' || field.field_type === 'textarea' ? 'md:col-span-2' : ''}>
      <Label className="flex items-center gap-1 mb-2">
        {label}
        {field.required && <span className="text-red-500">*</span>}
        {field.unit && field.field_type !== 'number' && (
          <span className="text-xs text-gray-400">({field.unit})</span>
        )}
      </Label>
      {renderInput()}
      {helpText && (
        <p className="text-xs text-gray-500 mt-1">{helpText}</p>
      )}
    </div>
  );
};

export default function SupplierMyProducts() {
  const { t, i18n } = useTranslation();
  const { API, user } = useContext(AppContext);
  const navigate = useNavigate();
  const { checkAdLimit, incrementAdCount } = useSubscriptionCheck();

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [shop, setShop] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name_fr: '',
    name_en: '',
    description_fr: '',
    description_en: '',
    category_id: '',
    location: '',
    price_new: '',
    price_used: '',
    price_rental: '',
    show_price: true,
    stock_new: 0,
    stock_used: 0,
    stock_rental: 0,
    delivery_time: '',
    delivery_time_type: 'fixed',
    availability_status: 'available',
    images: [],
    // New fields
    brand: '',
    model: '',
    year: '',
    condition_details: '',
    weight: '',
    dimensions: '',
    technical_specs: '',
    warranty: '',
    featured: false,
    // Dynamic template fields
    template_fields: {},
  });
  const [activeTab, setActiveTab] = useState('general');
  const [uploadingImages, setUploadingImages] = useState(false);
  const [togglingAll, setTogglingAll] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  
  // Template state
  const [currentTemplate, setCurrentTemplate] = useState(null);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [expandedSections, setExpandedSections] = useState({});

  // Import/Export CSV state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importFile, setImportFile] = useState(null);

  // Import/Export CSV functions
  const handleExportCSV = async () => {
    try {
      // Export only supplier's products by shop_id
      const shopParam = shop ? `?shop_id=${shop.id}` : '';
      window.open(`${API}/export/products${shopParam}`, '_blank');
      toast.success(i18n.language === 'fr' ? 'Export CSV démarré' : 'CSV export started');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de l\'export' : 'Export error');
    }
  };

  const handleDownloadTemplate = () => {
    window.open(`${API}/products/import/template`, '_blank');
  };

  const handleImportCSV = async () => {
    if (!importFile) {
      toast.error(i18n.language === 'fr' ? 'Veuillez sélectionner un fichier CSV' : 'Please select a CSV file');
      return;
    }

    setImporting(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', importFile);

      const response = await axios.post(`${API}/products/import?user_id=${user.id}`, formDataUpload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { message, errors } = response.data;
      toast.success(message);
      
      if (errors && errors.length > 0) {
        console.warn('Import errors:', errors);
        toast.warning(`${errors.length} erreur(s) lors de l'import`);
      }

      setImportDialogOpen(false);
      setImportFile(null);
      fetchShopAndProducts();
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || (i18n.language === 'fr' ? 'Erreur lors de l\'import' : 'Import error'));
    } finally {
      setImporting(false);
    }
  };

  const fetchShopAndProducts = useCallback(async () => {
    try {
      // récupérer la boutique du fournisseur par owner_id
      const shopsRes = await axios.get(`${API}/shops`);
      const supplierShop = shopsRes.data.find((s) => s.owner_id === user.id) || null;
      setShop(supplierShop || null);

      // charger uniquement les produits créés par ce user (ou de sa shop)
      let url = `${API}/products?include_status=all`;
      const res = await axios.get(url);
      let supplierProducts = res.data;
      if (supplierShop) {
        supplierProducts = supplierProducts.filter(
          (p) => p.shop_id === supplierShop.id || p.created_by_user_id === user.id
        );
      } else {
        supplierProducts = supplierProducts.filter((p) => p.created_by_user_id === user.id);
      }
      setProducts(supplierProducts);
    } catch (e) {
      console.error('Error fetching supplier products', e);
    }
  }, [API, user]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  }, [API]);

  // Fetch template when category changes
  const fetchTemplateForCategory = useCallback(async (categoryId) => {
    if (!categoryId) {
      setCurrentTemplate(null);
      return;
    }
    
    setLoadingTemplate(true);
    try {
      const response = await axios.get(`${API}/templates/by-category/${categoryId}`);
      const template = response.data;
      
      if (template && template.id !== 'default' && template.sections?.length > 0) {
        setCurrentTemplate(template);
        // Expand all sections by default
        const expanded = {};
        template.sections.forEach(s => { expanded[s.id] = true; });
        setExpandedSections(expanded);
      } else {
        setCurrentTemplate(null);
      }
    } catch (error) {
      console.error('Error fetching template:', error);
      setCurrentTemplate(null);
    } finally {
      setLoadingTemplate(false);
    }
  }, [API]);

  // Watch for category changes
  useEffect(() => {
    if (dialogOpen && formData.category_id) {
      fetchTemplateForCategory(formData.category_id);
    }
  }, [formData.category_id, dialogOpen, fetchTemplateForCategory]);

  useEffect(() => {
    if (!user) return;
    fetchShopAndProducts();
    fetchCategories();
  }, [user, fetchShopAndProducts, fetchCategories]);

  const resetForm = () => {
    setEditProduct(null);
    setActiveTab('general');
    setCurrentTemplate(null);
    setExpandedSections({});
    setFormData({
      name_fr: '',
      name_en: '',
      description_fr: '',
      description_en: '',
      category_id: '',
      location: '',
      price_new: '',
      price_used: '',
      price_rental: '',
      show_price: true,
      stock_new: 0,
      stock_used: 0,
      stock_rental: 0,
      delivery_time: '',
      delivery_time_type: 'fixed',
      availability_status: 'available',
      images: [],
      brand: '',
      model: '',
      year: '',
      condition_details: '',
      weight: '',
      dimensions: '',
      technical_specs: '',
      warranty: '',
      featured: false,
      template_fields: {},
    });
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;
    
    setUploadingImages(true);
    const uploadedUrls = [];
    
    for (const file of files) {
      try {
        const formDataUpload = new FormData();
        formDataUpload.append('file', file);
        
        const response = await axios.post(`${API}/upload-image`, formDataUpload, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });
        
        // Handle both 'url' and 'image_url' responses
        const imageUrl = response.data.url || response.data.image_url;
        if (imageUrl) {
          uploadedUrls.push(imageUrl);
        }
      } catch (error) {
        console.error('Error uploading image:', error);
        toast.error(i18n.language === 'fr' ? 'Erreur upload image' : 'Image upload error');
      }
    }
    
    if (uploadedUrls.length > 0) {
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, ...uploadedUrls]
      }));
      toast.success(i18n.language === 'fr' ? `${uploadedUrls.length} image(s) ajoutée(s)` : `${uploadedUrls.length} image(s) added`);
    }
    
    setUploadingImages(false);
    // Reset the input
    e.target.value = '';
  };

  const removeImage = (index) => {
    setFormData(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  const handleNewProduct = async () => {
    // Check subscription limit before allowing new product creation
    const check = await checkAdLimit('product');
    if (!check.allowed) {
      toast.error(check.message || 'Limite d\'annonces atteinte', {
        description: 'Passez à un plan supérieur pour créer plus d\'annonces',
        action: {
          label: 'Voir les offres',
          onClick: () => navigate('/pricing')
        }
      });
      return;
    }
    
    resetForm();
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Pour les fournisseurs (non-admin), vérifier qu'ils ont une shop
      if (!shop && user?.role !== 'admin') {
        toast.error(
          i18n.language === 'fr'
            ? "Aucune entreprise (fournisseur) associée à votre compte. Contactez l'administrateur."
            : 'No supplier shop associated with your account. Please contact admin.'
        );
        return;
      }

      const payload = {
        name_fr: formData.name_fr,
        name_en: formData.name_en || '',
        description_fr: formData.description_fr || '',
        description_en: formData.description_en || '',
        category_id: formData.category_id,
        shop_id: shop ? shop.id : null,
        location: formData.location || null,
        price_new: formData.price_new ? parseFloat(formData.price_new) : null,
        price_used: formData.price_used ? parseFloat(formData.price_used) : null,
        price_rental: formData.price_rental ? parseFloat(formData.price_rental) : null,
        stock_new: parseInt(formData.stock_new) || 0,
        stock_used: parseInt(formData.stock_used) || 0,
        stock_rental: parseInt(formData.stock_rental) || 0,
        delivery_time: formData.delivery_time || null,
        delivery_time_type: formData.delivery_time_type,
        availability_status: formData.availability_status,
        show_price: formData.show_price,
        images: formData.images || [],
        // New fields
        brand: formData.brand || null,
        model: formData.model || null,
        year: formData.year || null,
        condition_details: formData.condition_details || null,
        weight: formData.weight || null,
        dimensions: formData.dimensions || null,
        technical_specs: formData.technical_specs || null,
        warranty: formData.warranty || null,
        featured: formData.featured || false,
        created_by_user_id: user.id,
        status: user?.role === 'admin' ? 'active' : 'pending',
        // Dynamic template fields
        template_fields: formData.template_fields || {},
        template_id: currentTemplate?.id || null,
      };

      if (editProduct) {
        await axios.put(`${API}/products/${editProduct.id}`, payload);
        toast.success(
          i18n.language === 'fr' ? 'Produit mis à jour (en attente de validation)' : 'Product updated (pending validation)'
        );
      } else {
        // marquer comme en attente de validation côté backend (status sera géré dans /products route)
        await axios.post(`${API}/products`, payload);
        // Increment subscription ad count for new product
        await incrementAdCount('product');
        toast.success(
          i18n.language === 'fr' ? 'Produit créé, en attente de validation' : 'Product created, pending approval'
        );
      }

      setDialogOpen(false);
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error saving supplier product:', error);
      let errorMessage = i18n.language === 'fr' ? "Erreur lors de l'enregistrement" : 'Error saving product';
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg || e.message || JSON.stringify(e)).join(', ');
        } else if (typeof detail === 'object') {
          errorMessage = detail.msg || detail.message || JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const handleEdit = (product) => {
    setEditProduct(product);
    setFormData({
      name_fr: product.name_fr,
      name_en: product.name_en,
      description_fr: product.description_fr,
      description_en: product.description_en,
      category_id: product.category_id,
      location: product.location || '',
      price_new: product.price_new || '',
      price_used: product.price_used || '',
      price_rental: product.price_rental || '',
      show_price: product.show_price !== undefined ? product.show_price : true,
      stock_new: product.stock_new,
      stock_used: product.stock_used,
      stock_rental: product.stock_rental,
      availability_status: product.availability_status || 'available',
      delivery_time: product.delivery_time || '',
      delivery_time_type: product.delivery_time_type || 'fixed',
      images: product.images || [],
      // New fields
      brand: product.brand || '',
      model: product.model || '',
      year: product.year || '',
      condition_details: product.condition_details || '',
      weight: product.weight || '',
      dimensions: product.dimensions || '',
      technical_specs: product.technical_specs || '',
      warranty: product.warranty || '',
      featured: product.featured || false,
      // Dynamic template fields
      template_fields: product.template_fields || {},
    });
    setActiveTab('general');
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (
      !window.confirm(
        i18n.language === 'fr'
          ? 'Êtes-vous sûr de vouloir supprimer ce produit ?'
          : 'Are you sure you want to delete this product?'
      )
    ) {
      return;
    }

    try {
      await axios.delete(`${API}/products/${id}`);
      toast.success(i18n.language === 'fr' ? 'Produit supprimé' : 'Product deleted');
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors de la suppression' : 'Error deleting product');
    }
  };

  const handleToggleStatus = async (productId, currentStatus) => {
    setTogglingId(productId);
    const newStatus = (currentStatus === 'active' || currentStatus === 'approved' || currentStatus === 'available') 
      ? 'inactive' 
      : 'active';
    
    try {
      await axios.patch(`${API}/products/${productId}/status?status=${newStatus}`);
      toast.success(newStatus === 'inactive' 
        ? (i18n.language === 'fr' ? 'Produit désactivé' : 'Product deactivated')
        : (i18n.language === 'fr' ? 'Produit réactivé' : 'Product reactivated')
      );
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur lors du changement de statut' : 'Error changing status');
    } finally {
      setTogglingId(null);
    }
  };

  const handleToggleAllStatus = async (activate) => {
    if (products.length === 0) return;
    
    const confirmMessage = activate 
      ? (i18n.language === 'fr' ? 'Réactiver tous les produits ?' : 'Reactivate all products?')
      : (i18n.language === 'fr' ? 'Désactiver tous les produits ?' : 'Deactivate all products?');
    
    if (!window.confirm(confirmMessage)) return;
    
    setTogglingAll(true);
    const newStatus = activate ? 'active' : 'inactive';
    
    try {
      await Promise.all(
        products.map(p => axios.patch(`${API}/products/${p.id}/status?status=${newStatus}`))
      );
      toast.success(activate 
        ? (i18n.language === 'fr' ? 'Tous les produits ont été réactivés' : 'All products reactivated')
        : (i18n.language === 'fr' ? 'Tous les produits ont été désactivés' : 'All products deactivated')
      );
      fetchShopAndProducts();
    } catch (error) {
      console.error('Error toggling all:', error);
      toast.error(i18n.language === 'fr' ? 'Erreur' : 'Error');
    } finally {
      setTogglingAll(false);
    }
  };

  // Check if any products are active
  const hasActiveProducts = products.some(p => p.status === 'active' || p.status === 'approved' || p.status === 'available');
  const hasInactiveProducts = products.some(p => p.status === 'inactive');

  return (
    <div className="min-h-screen bg-[#F5F5F5]" data-testid="my-products-page">
      <div className="container mx-auto px-4 py-8">
        {/* Import/Export Products Section */}
        <div className="flex flex-wrap justify-end gap-2 mb-4">
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={handleExportCSV}
            className="text-sm"
            data-testid="export-csv-btn"
          >
            <FaDownload className="mr-2" />
            {i18n.language === 'fr' ? 'Exporter mes produits' : 'Export my products'}
          </Button>

          {/* Template Button */}
          <Button 
            variant="outline" 
            onClick={handleDownloadTemplate}
            className="text-sm"
          >
            <FaFileExcel className="mr-2 text-green-600" />
            {i18n.language === 'fr' ? 'Modèle CSV' : 'CSV Template'}
          </Button>

          {/* Import Dialog */}
          <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-[#0A2540] text-sm" data-testid="import-csv-btn">
                <FaUpload className="mr-2" />
                {i18n.language === 'fr' ? 'Importer CSV' : 'Import CSV'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FaFileExcel className="text-green-600" />
                  {i18n.language === 'fr' ? 'Import CSV Produits' : 'CSV Product Import'}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <p className="text-sm text-blue-800 mb-2">
                    <strong>{i18n.language === 'fr' ? 'Format supporté :' : 'Supported format:'}</strong> {i18n.language === 'fr' 
                      ? 'Fichier CSV avec colonnes standards + colonnes dynamiques (préfixe tf_)' 
                      : 'CSV file with standard columns + dynamic columns (tf_ prefix)'}
                  </p>
                  <p className="text-xs text-blue-600 mt-2">
                    {i18n.language === 'fr' 
                      ? '💡 Astuce : Utilisez le modèle CSV pour avoir toutes les colonnes correctement formatées' 
                      : '💡 Tip: Use the CSV template to have all columns properly formatted'}
                  </p>
                </div>
                
                <div>
                  <Label>{i18n.language === 'fr' ? 'Fichier CSV' : 'CSV File'}</Label>
                  <Input
                    type="file"
                    accept=".csv"
                    onChange={(e) => setImportFile(e.target.files[0])}
                    className="mt-2"
                  />
                  {importFile && (
                    <p className="text-sm text-gray-500 mt-1">
                      {i18n.language === 'fr' ? 'Fichier sélectionné : ' : 'Selected file: '}{importFile.name}
                    </p>
                  )}
                </div>

                <div className="flex justify-end gap-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setImportDialogOpen(false); setImportFile(null); }}
                  >
                    {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                  </Button>
                  <Button 
                    onClick={handleImportCSV}
                    disabled={!importFile || importing}
                    className="bg-[#635BFF]"
                  >
                    {importing ? (
                      <><FaSpinner className="animate-spin mr-2" /> {i18n.language === 'fr' ? 'Import en cours...' : 'Importing...'}</>
                    ) : (
                      <><FaUpload className="mr-2" /> {i18n.language === 'fr' ? 'Importer' : 'Import'}</>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-4xl font-bold text-[#0A2540]">
              {i18n.language === 'fr' ? 'Mes Produits' : 'My Products'}
            </h1>
            {shop && (
              <p className="text-gray-600 text-sm mt-1">
                {i18n.language === 'fr'
                  ? `Entreprise : ${shop.name}`
                  : `Company: ${shop.name}`}
              </p>
            )}
          </div>

          <div className="flex gap-2 flex-wrap">
            {/* Toggle All Buttons */}
            {products.length > 0 && (
              <>
                {hasActiveProducts && (
                  <Button 
                    variant="outline" 
                    onClick={() => handleToggleAllStatus(false)}
                    disabled={togglingAll}
                    className="text-[#00D4FF] border-orange-500 hover:bg-orange-50"
                  >
                    {togglingAll ? <FaSpinner className="animate-spin mr-2" /> : <FaPause className="mr-2" />}
                    {i18n.language === 'fr' ? 'Tout désactiver' : 'Deactivate all'}
                  </Button>
                )}
                {hasInactiveProducts && (
                  <Button 
                    variant="outline" 
                    onClick={() => handleToggleAllStatus(true)}
                    disabled={togglingAll}
                    className="text-[#635BFF] border-[#635BFF] hover:bg-[#635BFF]/5"
                  >
                    {togglingAll ? <FaSpinner className="animate-spin mr-2" /> : <FaPlay className="mr-2" />}
                    {i18n.language === 'fr' ? 'Tout réactiver' : 'Reactivate all'}
                  </Button>
                )}
              </>
            )}
            
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={handleNewProduct} className="bg-[#635BFF]">
                  <FaPlus className="mr-2" />
                  {i18n.language === 'fr' ? 'Nouveau produit' : 'New product'}
                </Button>
              </DialogTrigger>

              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                <DialogTitle className="text-2xl">
                  {editProduct
                    ? i18n.language === 'fr'
                      ? '✏️ Modifier le produit'
                      : '✏️ Edit product'
                    : i18n.language === 'fr'
                    ? '➕ Nouveau produit'
                    : '➕ New product'}
                </DialogTitle>
              </DialogHeader>

              {/* Tabs Navigation */}
              <div className="flex flex-wrap gap-2 border-b pb-2 mb-4">
                {[
                  { id: 'general', label: i18n.language === 'fr' ? '📝 Général' : '📝 General' },
                  { id: 'pricing', label: i18n.language === 'fr' ? '💰 Prix & Stock' : '💰 Pricing & Stock' },
                  { id: 'specs', label: i18n.language === 'fr' ? '📋 Fiche technique' : '📋 Specifications', hasTemplate: true },
                  { id: 'details', label: i18n.language === 'fr' ? '📝 Détails' : '📝 Details' },
                  { id: 'images', label: i18n.language === 'fr' ? '🖼️ Images' : '🖼️ Images' },
                ].map(tab => (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
                      activeTab === tab.id
                        ? 'bg-[#0A2540] text-white'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                  >
                    {tab.label}
                    {tab.hasTemplate && currentTemplate && (
                      <span className="bg-[#635BFF] text-white text-xs px-1.5 py-0.5 rounded-full">
                        {currentTemplate.fields?.length || 0}
                      </span>
                    )}
                  </button>
                ))}
              </div>

              <form onSubmit={handleSubmit} className="space-y-4">
                {/* TAB: GENERAL */}
                {activeTab === 'general' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="flex items-center gap-1">
                          {i18n.language === 'fr' ? 'Nom (FR)' : 'Name (FR)'} <span className="text-red-500">*</span>
                        </Label>
                        <Input
                          value={formData.name_fr}
                          onChange={(e) => setFormData({ ...formData, name_fr: e.target.value })}
                          placeholder={i18n.language === 'fr' ? 'Nom du produit en français' : 'Product name in French'}
                          required
                        />
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Nom (EN)' : 'Name (EN)'}</Label>
                        <Input
                          value={formData.name_en}
                          onChange={(e) => setFormData({ ...formData, name_en: e.target.value })}
                          placeholder={i18n.language === 'fr' ? 'Nom du produit en anglais' : 'Product name in English'}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Description (FR)' : 'Description (FR)'}</Label>
                        <textarea
                          className="w-full min-h-[100px] p-3 border rounded-md resize-y"
                          value={formData.description_fr}
                          onChange={(e) => setFormData({ ...formData, description_fr: e.target.value })}
                          placeholder={i18n.language === 'fr' ? 'Description détaillée du produit...' : 'Detailed product description...'}
                        />
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Description (EN)' : 'Description (EN)'}</Label>
                        <textarea
                          className="w-full min-h-[100px] p-3 border rounded-md resize-y"
                          value={formData.description_en}
                          onChange={(e) => setFormData({ ...formData, description_en: e.target.value })}
                          placeholder={i18n.language === 'fr' ? 'Description en anglais...' : 'Description in English...'}
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Catégorie' : 'Category'}</Label>
                        <Select
                          value={formData.category_id}
                          onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder={i18n.language === 'fr' ? 'Sélectionner une catégorie' : 'Select a category'} />
                          </SelectTrigger>
                          <SelectContent>
                            {categories.map((cat) => (
                              <SelectItem key={cat.id} value={cat.id}>
                                {i18n.language === 'fr' ? cat.name_fr : cat.name_en}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Localisation' : 'Location'}</Label>
                        <Input
                          value={formData.location}
                          onChange={(e) => setFormData({ ...formData, location: e.target.value })}
                          placeholder="ex: Paris, France"
                        />
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Marque' : 'Brand'}</Label>
                        <Input
                          value={formData.brand}
                          onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                          placeholder="ex: Caterpillar, Soilmec..."
                        />
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Modèle' : 'Model'}</Label>
                        <Input
                          value={formData.model}
                          onChange={(e) => setFormData({ ...formData, model: e.target.value })}
                          placeholder="ex: SR-75, B180..."
                        />
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Année' : 'Year'}</Label>
                        <Input
                          type="number"
                          value={formData.year}
                          onChange={(e) => setFormData({ ...formData, year: e.target.value })}
                          placeholder="ex: 2022"
                        />
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/30">
                      <Checkbox
                        checked={formData.featured}
                        onCheckedChange={(checked) => setFormData({ ...formData, featured: checked })}
                      />
                      <Label className="cursor-pointer font-medium text-[#5248d9]">
                        ⭐ {i18n.language === 'fr' ? 'Produit mis en avant (Featured)' : 'Featured product'}
                      </Label>
                    </div>
                  </div>
                )}

                {/* TAB: PRICING & STOCK */}
                {activeTab === 'pricing' && (
                  <div className="space-y-6">
                    {/* Prix */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        💰 {i18n.language === 'fr' ? 'Tarification' : 'Pricing'}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg bg-blue-50">
                          <Label className="text-blue-700 font-medium">{i18n.language === 'fr' ? 'Prix Neuf (€)' : 'New Price (€)'}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_new}
                            onChange={(e) => setFormData({ ...formData, price_new: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="p-4 border rounded-lg bg-orange-50">
                          <Label className="text-orange-700 font-medium">{i18n.language === 'fr' ? 'Prix Occasion (€)' : 'Used Price (€)'}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_used}
                            onChange={(e) => setFormData({ ...formData, price_used: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                        <div className="p-4 border rounded-lg bg-[#635BFF]/5">
                          <Label className="text-[#5248d9] font-medium">{i18n.language === 'fr' ? 'Prix Location (€/jour)' : 'Rental Price (€/day)'}</Label>
                          <Input
                            type="number"
                            step="0.01"
                            value={formData.price_rental}
                            onChange={(e) => setFormData({ ...formData, price_rental: e.target.value })}
                            className="mt-2"
                            placeholder="0.00"
                          />
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 p-4 bg-gray-50 rounded-lg">
                      <Checkbox
                        checked={formData.show_price}
                        onCheckedChange={(checked) => setFormData({ ...formData, show_price: checked })}
                      />
                      <Label className="cursor-pointer">
                        👁️ {i18n.language === 'fr' ? 'Afficher le prix sur la fiche produit' : 'Show price on product page'}
                      </Label>
                    </div>

                    {/* Stock */}
                    <div>
                      <h3 className="font-semibold text-lg mb-3 flex items-center gap-2">
                        📦 {i18n.language === 'fr' ? 'Stock' : 'Inventory'}
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div className="p-4 border rounded-lg">
                          <Label>{i18n.language === 'fr' ? 'Stock Neuf' : 'New Stock'}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_new}
                            onChange={(e) => setFormData({ ...formData, stock_new: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                        <div className="p-4 border rounded-lg">
                          <Label>{i18n.language === 'fr' ? 'Stock Occasion' : 'Used Stock'}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_used}
                            onChange={(e) => setFormData({ ...formData, stock_used: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                        <div className="p-4 border rounded-lg">
                          <Label>{i18n.language === 'fr' ? 'Stock Location' : 'Rental Stock'}</Label>
                          <Input
                            type="number"
                            min="0"
                            value={formData.stock_rental}
                            onChange={(e) => setFormData({ ...formData, stock_rental: e.target.value })}
                            className="mt-2"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Disponibilité */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Statut de disponibilité' : 'Availability Status'}</Label>
                        <Select
                          value={formData.availability_status}
                          onValueChange={(value) => setFormData({ ...formData, availability_status: value })}
                        >
                          <SelectTrigger className="mt-2">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="available">✅ {i18n.language === 'fr' ? 'Disponible' : 'Available'}</SelectItem>
                            <SelectItem value="on_order">📋 {i18n.language === 'fr' ? 'Sur commande' : 'On order'}</SelectItem>
                            <SelectItem value="out_of_stock">❌ {i18n.language === 'fr' ? 'Rupture de stock' : 'Out of stock'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Délai de livraison' : 'Delivery Time'}</Label>
                        <div className="grid grid-cols-2 gap-2 mt-2">
                          <Select
                            value={formData.delivery_time_type}
                            onValueChange={(value) => setFormData({ ...formData, delivery_time_type: value })}
                          >
                            <SelectTrigger>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="fixed">{i18n.language === 'fr' ? 'Prédéfini' : 'Fixed'}</SelectItem>
                              <SelectItem value="on_demand">{i18n.language === 'fr' ? 'À déterminer' : 'To be determined'}</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.delivery_time_type === 'fixed' ? (
                            <Select
                              value={formData.delivery_time}
                              onValueChange={(value) => setFormData({ ...formData, delivery_time: value })}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={i18n.language === 'fr' ? 'Délai' : 'Time'} />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="24h">24h</SelectItem>
                                <SelectItem value="48h">48h</SelectItem>
                                <SelectItem value="72h">72h</SelectItem>
                                <SelectItem value="7j">7 {i18n.language === 'fr' ? 'jours' : 'days'}</SelectItem>
                                <SelectItem value="15j">15 {i18n.language === 'fr' ? 'jours' : 'days'}</SelectItem>
                                <SelectItem value="1m">1 {i18n.language === 'fr' ? 'mois' : 'month'}</SelectItem>
                              </SelectContent>
                            </Select>
                          ) : (
                            <Input
                              placeholder="Ex: 2-3 semaines"
                              value={formData.delivery_time}
                              onChange={(e) => setFormData({ ...formData, delivery_time: e.target.value })}
                            />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* TAB: SPECS (Dynamic Template Fields) */}
                {activeTab === 'specs' && (
                  <div className="space-y-4">
                    {loadingTemplate ? (
                      <div className="p-8 text-center">
                        <FaSpinner className="animate-spin text-2xl text-[#635BFF] mx-auto mb-2" />
                        <p className="text-gray-500">{i18n.language === 'fr' ? 'Chargement du template...' : 'Loading template...'}</p>
                      </div>
                    ) : !formData.category_id ? (
                      <div className="p-8 text-center bg-yellow-50 rounded-lg border border-yellow-200">
                        <FaClipboardList className="text-4xl text-yellow-500 mx-auto mb-3" />
                        <p className="font-medium text-yellow-800">
                          {i18n.language === 'fr' ? 'Sélectionnez d\'abord une catégorie' : 'Select a category first'}
                        </p>
                        <p className="text-sm text-yellow-600 mt-1">
                          {i18n.language === 'fr' 
                            ? 'La fiche technique sera adaptée à la catégorie choisie' 
                            : 'The specification sheet will be adapted to the chosen category'}
                        </p>
                        <Button 
                          type="button" 
                          variant="outline" 
                          className="mt-4"
                          onClick={() => setActiveTab('general')}
                        >
                          ← {i18n.language === 'fr' ? 'Retour aux infos générales' : 'Back to general info'}
                        </Button>
                      </div>
                    ) : !currentTemplate ? (
                      <div className="p-8 text-center bg-gray-50 rounded-lg border border-gray-200">
                        <Box className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                        <p className="font-medium text-gray-700">
                          {i18n.language === 'fr' ? 'Pas de fiche technique spécifique' : 'No specific specification sheet'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {i18n.language === 'fr' 
                            ? 'Cette catégorie utilise le formulaire standard. Utilisez l\'onglet "Détails" pour les spécifications.' 
                            : 'This category uses the standard form. Use the "Details" tab for specifications.'}
                        </p>
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {/* Template Info */}
                        <div className="p-4 bg-[#635BFF]/5 rounded-lg border border-[#635BFF]/20">
                          <div className="flex items-center gap-3">
                            <FaClipboardList className="text-[#635BFF]" />
                            <div>
                              <p className="font-medium text-[#0A2540]">
                                {i18n.language === 'fr' ? currentTemplate.name_fr : currentTemplate.name_en}
                              </p>
                              <p className="text-sm text-gray-500">
                                {currentTemplate.sections?.length || 0} sections • {currentTemplate.fields?.length || 0} {i18n.language === 'fr' ? 'champs' : 'fields'}
                              </p>
                            </div>
                          </div>
                        </div>

                        {/* Dynamic Sections & Fields */}
                        {(currentTemplate.sections || [])
                          .sort((a, b) => (a.position || 0) - (b.position || 0))
                          .map((section) => {
                            const SectionIcon = SECTION_ICONS[section.icon_lucide] || Box;
                            const sectionFields = (currentTemplate.fields || [])
                              .filter(f => f.section_id === section.id && f.visible !== false)
                              .sort((a, b) => (a.position || 0) - (b.position || 0));
                            
                            if (sectionFields.length === 0) return null;
                            
                            return (
                              <div key={section.id} className="border rounded-lg overflow-hidden">
                                {/* Section Header */}
                                <button
                                  type="button"
                                  onClick={() => setExpandedSections(prev => ({ ...prev, [section.id]: !prev[section.id] }))}
                                  className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 transition-colors"
                                >
                                  <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 bg-[#635BFF]/10 rounded-lg flex items-center justify-center">
                                      <SectionIcon size={16} className="text-[#635BFF]" />
                                    </div>
                                    <span className="font-medium text-[#0A2540]">
                                      {i18n.language === 'fr' ? section.name_fr : section.name_en}
                                    </span>
                                    <span className="text-xs text-gray-500">({sectionFields.length})</span>
                                  </div>
                                  {expandedSections[section.id] ? (
                                    <ChevronDown size={20} className="text-gray-400" />
                                  ) : (
                                    <ChevronRight size={20} className="text-gray-400" />
                                  )}
                                </button>
                                
                                {/* Section Fields */}
                                {expandedSections[section.id] && (
                                  <div className="p-4 space-y-4">
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                      {sectionFields.map((field) => (
                                        <DynamicField
                                          key={field.id}
                                          field={field}
                                          value={formData.template_fields[field.name]}
                                          onChange={(value) => setFormData(prev => ({
                                            ...prev,
                                            template_fields: {
                                              ...prev.template_fields,
                                              [field.name]: value
                                            }
                                          }))}
                                          language={i18n.language}
                                        />
                                      ))}
                                    </div>
                                  </div>
                                )}
                              </div>
                            );
                          })}
                      </div>
                    )}
                  </div>
                )}

                {/* TAB: DETAILS */}
                {activeTab === 'details' && (
                  <div className="space-y-4">
                    <div>
                      <Label>{i18n.language === 'fr' ? 'État / Condition' : 'Condition Details'}</Label>
                      <textarea
                        className="w-full min-h-[80px] p-3 border rounded-md resize-y mt-2"
                        value={formData.condition_details}
                        onChange={(e) => setFormData({ ...formData, condition_details: e.target.value })}
                        placeholder={i18n.language === 'fr' ? 'Ex: Excellent état, révisé en 2023, moins de 500h d\'utilisation...' : 'Ex: Excellent condition, serviced in 2023, less than 500h of use...'}
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Poids' : 'Weight'}</Label>
                        <Input
                          value={formData.weight}
                          onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                          placeholder="ex: 15 tonnes, 250 kg..."
                          className="mt-2"
                        />
                      </div>
                      <div>
                        <Label>{i18n.language === 'fr' ? 'Dimensions' : 'Dimensions'}</Label>
                        <Input
                          value={formData.dimensions}
                          onChange={(e) => setFormData({ ...formData, dimensions: e.target.value })}
                          placeholder="ex: 12m x 3m x 4m (LxlxH)"
                          className="mt-2"
                        />
                      </div>
                    </div>

                    <div>
                      <Label>{i18n.language === 'fr' ? 'Spécifications techniques' : 'Technical Specifications'}</Label>
                      <textarea
                        className="w-full min-h-[120px] p-3 border rounded-md resize-y mt-2 font-mono text-sm"
                        value={formData.technical_specs}
                        onChange={(e) => setFormData({ ...formData, technical_specs: e.target.value })}
                        placeholder={i18n.language === 'fr' 
                          ? "• Puissance: 350 kW\n• Profondeur max: 60m\n• Couple: 200 kNm\n• Diamètre: jusqu'à 2000mm" 
                          : "• Power: 350 kW\n• Max depth: 60m\n• Torque: 200 kNm\n• Diameter: up to 2000mm"}
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {i18n.language === 'fr' ? 'Une spécification par ligne, utilisez • pour les puces' : 'One specification per line, use • for bullet points'}
                      </p>
                    </div>

                    <div>
                      <Label>{i18n.language === 'fr' ? 'Garantie' : 'Warranty'}</Label>
                      <Input
                        value={formData.warranty}
                        onChange={(e) => setFormData({ ...formData, warranty: e.target.value })}
                        placeholder={i18n.language === 'fr' ? 'ex: 2 ans, 12 mois pièces et main d\'œuvre...' : 'ex: 2 years, 12 months parts and labor...'}
                        className="mt-2"
                      />
                    </div>
                  </div>
                )}

                {/* TAB: IMAGES */}
                {activeTab === 'images' && (
                  <div className="space-y-4">
                    {/* Upload Zone */}
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-[#0A2540] transition-colors">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageUpload}
                        className="hidden"
                        id="image-upload"
                      />
                      <label htmlFor="image-upload" className="cursor-pointer">
                        <div className="text-4xl mb-2">📷</div>
                        <p className="font-medium text-gray-700">
                          {i18n.language === 'fr' ? 'Cliquez pour ajouter des images' : 'Click to add images'}
                        </p>
                        <p className="text-sm text-gray-500 mt-1">
                          {i18n.language === 'fr' ? 'ou glissez-déposez vos fichiers ici' : 'or drag and drop files here'}
                        </p>
                      </label>
                      {uploadingImages && (
                        <div className="mt-3">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#0A2540] mx-auto"></div>
                          <p className="text-sm text-gray-500 mt-1">{i18n.language === 'fr' ? 'Upload en cours...' : 'Uploading...'}</p>
                        </div>
                      )}
                    </div>

                    {/* Image Preview Grid */}
                    {formData.images.length > 0 && (
                      <div>
                        <Label className="mb-2 block">{i18n.language === 'fr' ? 'Images ajoutées' : 'Added images'} ({formData.images.length})</Label>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                          {formData.images.map((url, index) => (
                            <div key={index} className="relative group">
                              <img
                                src={url.startsWith('http') ? url : `${API}${url}`}
                                alt={`Product ${index + 1}`}
                                className="w-full h-24 object-cover rounded-lg border"
                                onError={(e) => { e.target.src = 'https://via.placeholder.com/150?text=Error'; }}
                              />
                              <button
                                type="button"
                                onClick={() => removeImage(index)}
                                className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                ×
                              </button>
                              {index === 0 && (
                                <span className="absolute bottom-1 left-1 bg-[#0A2540] text-white text-xs px-2 py-0.5 rounded">
                                  {i18n.language === 'fr' ? 'Principal' : 'Main'}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Manual URL Input */}
                    <div>
                      <Label>{i18n.language === 'fr' ? 'Ou ajoutez des URLs d\'images' : 'Or add image URLs'}</Label>
                      <textarea
                        className="w-full min-h-[80px] p-3 border rounded-md text-sm mt-2"
                        placeholder={i18n.language === 'fr' 
                          ? "https://example.com/image1.jpg\nhttps://example.com/image2.jpg" 
                          : "https://example.com/image1.jpg\nhttps://example.com/image2.jpg"}
                        value={formData.images.filter(url => url.startsWith('http')).join('\n')}
                        onChange={(e) => {
                          const urls = e.target.value.split('\n').map(url => url.trim()).filter(Boolean);
                          const uploadedImages = formData.images.filter(url => !url.startsWith('http'));
                          setFormData({ ...formData, images: [...uploadedImages, ...urls] });
                        }}
                      />
                    </div>
                  </div>
                )}

                {/* Navigation & Submit Buttons */}
                <div className="flex justify-between items-center pt-4 border-t mt-6">
                  <div className="flex gap-2">
                    {activeTab !== 'general' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const tabs = ['general', 'pricing', 'specs', 'details', 'images'];
                          const currentIndex = tabs.indexOf(activeTab);
                          setActiveTab(tabs[currentIndex - 1]);
                        }}
                      >
                        ← {i18n.language === 'fr' ? 'Précédent' : 'Previous'}
                      </Button>
                    )}
                    {activeTab !== 'images' && (
                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => {
                          const tabs = ['general', 'pricing', 'specs', 'details', 'images'];
                          const currentIndex = tabs.indexOf(activeTab);
                          setActiveTab(tabs[currentIndex + 1]);
                        }}
                      >
                        {i18n.language === 'fr' ? 'Suivant' : 'Next'} →
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-3">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setDialogOpen(false)}
                    >
                      {i18n.language === 'fr' ? 'Annuler' : 'Cancel'}
                    </Button>
                    <Button type="submit" className="bg-[#0A2540]">
                      {i18n.language === 'fr' ? 'Enregistrer' : 'Save'}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        </div>

        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="p-4 text-left">Produit</th>
                    <th className="p-4 text-left">Catégorie</th>
                    <th className="p-4 text-left">Localisation</th>
                    <th className="p-4 text-left">Prix</th>
                    <th className="p-4 text-left">Stock</th>
                    <th className="p-4 text-left">Statut</th>
                    <th className="p-4 text-left">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {products.map((product, index) => (
                    <tr key={product.id} className="border-t">
                      <td className="p-4">
                        <div className="font-semibold">
                          {i18n.language === 'fr' ? product.name_fr : product.name_en}
                        </div>
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {categories.find((c) => c.id === product.category_id)?.[
                          i18n.language === 'fr' ? 'name_fr' : 'name_en'
                        ] || '-'}
                      </td>
                      <td className="p-4 text-sm text-gray-600">
                        {product.location || '-'}
                      </td>
                      <td className="p-4 text-sm">
                        {product.price_new && <div>Neuf: €{product.price_new}</div>}
                        {product.price_used && <div>Occasion: €{product.price_used}</div>}
                        {product.price_rental && (
                          <div>Location: €{product.price_rental}/{i18n.language === 'fr' ? 'jour' : 'day'}</div>
                        )}
                      </td>
                      <td className="p-4 text-sm">
                        {product.stock_new > 0 && <div>Neuf: {product.stock_new}</div>}
                        {product.stock_used > 0 && <div>Occasion: {product.stock_used}</div>}
                        {product.stock_rental > 0 && <div>Location: {product.stock_rental}</div>}
                      </td>
                      <td className="p-4 text-sm">
                        {product.status === 'active' && (
                          <span className="px-2 py-1 bg-[#635BFF]/10 text-[#5248d9] rounded-full text-xs">
                            {i18n.language === 'fr' ? 'Publié' : 'Published'}
                          </span>
                        )}
                        {product.status === 'pending' && (
                          <span className="px-2 py-1 bg-[#00D4FF]/10 text-orange-700 rounded-full text-xs">
                            {i18n.language === 'fr' ? 'En validation' : 'Pending approval'}
                          </span>
                        )}
                        {product.status === 'rejected' && (
                          <span className="px-2 py-1 bg-red-100 text-red-700 rounded-full text-xs">
                            {i18n.language === 'fr' ? 'Rejeté' : 'Rejected'}
                          </span>
                        )}
                        {product.status === 'inactive' && (
                          <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded-full text-xs">
                            {i18n.language === 'fr' ? 'Désactivé' : 'Inactive'}
                          </span>
                        )}
                      </td>
                      <td className="p-4">
                        <div className="flex gap-2">
                          {/* Toggle Status Button */}
                          {(product.status === 'active' || product.status === 'approved') ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleToggleStatus(product.id, product.status)}
                              disabled={togglingId === product.id}
                              className="text-[#00D4FF] border-orange-500 hover:bg-orange-50"
                              title={i18n.language === 'fr' ? 'Désactiver' : 'Deactivate'}
                            >
                              {togglingId === product.id ? <FaSpinner className="animate-spin" /> : <FaPause />}
                            </Button>
                          ) : product.status === 'inactive' ? (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleToggleStatus(product.id, product.status)}
                              disabled={togglingId === product.id}
                              className="text-[#635BFF] border-[#635BFF] hover:bg-[#635BFF]/5"
                              title={i18n.language === 'fr' ? 'Réactiver' : 'Reactivate'}
                            >
                              {togglingId === product.id ? <FaSpinner className="animate-spin" /> : <FaPlay />}
                            </Button>
                          ) : null}
                          
                          <Button size="sm" variant="outline" onClick={() => handleEdit(product)}>
                            <FaEdit />
                          </Button>
                          <Button
                            size="sm"
                            variant="destructive"
                            onClick={() => handleDelete(product.id)}
                          >
                            <FaTrash />
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
